import { useCallback, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CREATE_MODAL_TYPES } from '@savgroup-front-common/constants/control';
import { selectSellers } from '@savgroup-front-common/core/domains/sellers/selectors';

import { ROUTES, SPARE_PART_ROUTES } from '@/constants/routing';
import { useAppContext } from '@/view/app/App.context';

import { createModalAnimationDuration } from '../CreateButton.constants';
import {
  createModalInitialState,
  createModalStateReducer,
} from '../CreateButton.reducers';
import {
  CREATE_MENU_ITEM_TYPE,
  CreateMenuItem,
  MODAL_STATE_ACTION_TYPES,
} from '../CreateButton.types';

import useAuthorizedCreateOptions from './useAuthorizedCreateOptions';
import useAuthorizedCreateShortcuts from './useAuthorizedCreateShortcuts';

const useCreateButton = () => {
  const navigate = useNavigate();
  const sellers = useSelector(selectSellers);
  const { selectedSeller } = useAppContext();
  const [state, dispatch] = useReducer(
    createModalStateReducer,
    null,
    createModalInitialState,
  );

  const { isMenuOpen, modalType } = state;

  const onCreateModalOpen = useCallback(({ key }: CreateMenuItem) => {
    dispatch({
      type: MODAL_STATE_ACTION_TYPES.OPEN_CREATE_MODAL,
      payload: { key: key as CREATE_MODAL_TYPES },
    });
  }, []);

  const onCreateModalClose = useCallback(async () => {
    dispatch({
      type: MODAL_STATE_ACTION_TYPES.CLOSE_CREATE_MODAL,
    });

    return new Promise((resolve) =>
      setTimeout(resolve, createModalAnimationDuration),
    );
  }, []);

  const authorizedOptions = useAuthorizedCreateOptions({ onCreateModalOpen });

  useAuthorizedCreateShortcuts({
    onCreateModalOpen,
    autorizedCreateOptions: authorizedOptions,
  });

  const menuItems = authorizedOptions.filter(
    (option) =>
      option.type === CREATE_MENU_ITEM_TYPE.BUTTON ||
      option.type === CREATE_MENU_ITEM_TYPE.LINK,
  );

  const onToggleMenu = useCallback(() => {
    dispatch({
      type: MODAL_STATE_ACTION_TYPES.TOGGLE_MENU,
    });
  }, []);
  const onMenuOpenChange = useCallback((isOpen: boolean) => {
    dispatch({
      type: isOpen
        ? MODAL_STATE_ACTION_TYPES.OPEN_MENU
        : MODAL_STATE_ACTION_TYPES.CLOSE_MENU,
    });
  }, []);
  const handleButtonClick = useCallback(() => {
    if (menuItems.length > 1) {
      onToggleMenu();
    } else if (
      menuItems.length === 1 &&
      menuItems[0].key === CREATE_MODAL_TYPES.NEW_CREATE_FILE
    ) {
      navigate(ROUTES.NEW_CREATE_FILE);
    } else if (
      menuItems.length === 1 &&
      menuItems[0].key === CREATE_MODAL_TYPES.SPARE_PART_REQUEST
    ) {
      navigate(SPARE_PART_ROUTES.CREATE_REQUEST);
    } else {
      onCreateModalOpen(menuItems[0]);
    }
  }, [menuItems, navigate, onCreateModalOpen, onToggleMenu]);

  const isAllowNewFiles = selectedSeller?.allowNewFiles;
  const hasNoOptions = menuItems.length === 0 || isAllowNewFiles === false;
  const hasMultipleOptions = menuItems.length > 1;
  const isDisabled = hasNoOptions || sellers.length === 0;
  const isOnlySparePartRequest =
    menuItems.length === 1 &&
    menuItems[0].key === CREATE_MODAL_TYPES.SPARE_PART_REQUEST;

  return {
    onToggleMenu,
    onMenuOpenChange,
    isMenuOpen,
    onCreateModalClose,
    handleButtonClick,
    menuItems,
    modalType,
    authorizedOptions,
    hasMultipleOptions,
    isDisabled,
    isOnlySparePartRequest,
  };
};

export default useCreateButton;
